import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useSession } from '../lib/session';
import './SignIn.css';

type Mode = 'password' | 'magic' | 'forgot';
type Status = 'idle' | 'sending' | 'sent' | 'reset-sent' | 'error' | 'not-invited';

export default function SignIn() {
  const [mode, setMode] = useState<Mode>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [errorText, setErrorText] = useState('');
  const navigate = useNavigate();
  const { isAuthed, loading } = useSession();

  if (loading) return null;
  if (isAuthed) {
    navigate('/', { replace: true });
    return null;
  }

  async function whitelistCheck(normalizedEmail: string): Promise<
    { kind: 'ok' } | { kind: 'not_invited' } | { kind: 'error'; detail?: string }
  > {
    try {
      const res = await fetch('/api/sign-in', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email: normalizedEmail }),
      });
      if (res.status === 403) return { kind: 'not_invited' };
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        return { kind: 'error', detail: body?.detail ?? body?.error ?? `HTTP ${res.status}` };
      }
      return { kind: 'ok' };
    } catch (err: any) {
      return { kind: 'error', detail: err?.message ?? 'Network error' };
    }
  }

  async function submitPassword(e: React.FormEvent) {
    e.preventDefault();
    if (!email || !password) return;
    const normalized = email.trim().toLowerCase();
    setStatus('sending');
    setErrorText('');

    const gate = await whitelistCheck(normalized);
    if (gate.kind === 'not_invited') { setStatus('not-invited'); return; }
    if (gate.kind === 'error') {
      setErrorText(`Sign-in precheck failed: ${gate.detail ?? 'unknown error'}`);
      setStatus('error');
      return;
    }

    const { error } = await supabase.auth.signInWithPassword({ email: normalized, password });
    if (error) {
      // If the user has never set a password (magic-link-only to date), the
      // password sign-in will error. Surface a helpful hint.
      setErrorText(
        error.message.toLowerCase().includes('invalid')
          ? "That email / password doesn't match. If you've only used one time links before, switch to the One Time Link tab, then set a password in Profile."
          : error.message,
      );
      setStatus('error');
      return;
    }
    navigate('/', { replace: true });
  }

  async function submitMagic(e: React.FormEvent) {
    e.preventDefault();
    if (!email) return;
    const normalized = email.trim().toLowerCase();
    setStatus('sending');
    setErrorText('');

    const gate = await whitelistCheck(normalized);
    if (gate.kind === 'not_invited') { setStatus('not-invited'); return; }
    if (gate.kind === 'error') {
      setErrorText(`Sign-in precheck failed: ${gate.detail ?? 'unknown error'}`);
      setStatus('error');
      return;
    }

    const { error } = await supabase.auth.signInWithOtp({
      email: normalized,
      options: { emailRedirectTo: `${window.location.origin}/auth/callback` },
    });
    if (error) {
      setErrorText(error.message);
      setStatus('error');
      return;
    }
    setStatus('sent');
  }

  async function submitForgot(e: React.FormEvent) {
    e.preventDefault();
    if (!email) return;
    const normalized = email.trim().toLowerCase();
    setStatus('sending');
    setErrorText('');

    // Same precheck as sign-in: avoid spamming unknown addresses and
    // give a clear "not invited" message instead of a silent no-op.
    const gate = await whitelistCheck(normalized);
    if (gate.kind === 'not_invited') { setStatus('not-invited'); return; }
    if (gate.kind === 'error') {
      setErrorText(`Reset precheck failed: ${gate.detail ?? 'unknown error'}`);
      setStatus('error');
      return;
    }

    const { error } = await supabase.auth.resetPasswordForEmail(normalized, {
      // ?next= is read by AuthCallback after the PKCE exchange so the user
      // lands on the set-password screen instead of the home feed.
      redirectTo: `${window.location.origin}/auth/callback?next=/reset-password`,
    });
    if (error) {
      setErrorText(error.message);
      setStatus('error');
      return;
    }
    setStatus('reset-sent');
  }

  return (
    <div className="signin">
      <aside className="signin__stripe" aria-hidden="true">
        <div className="signin__logo">
          {/* Ring = the icon-only mark (centred in its frame → clean spin).
              Wordmark = the real "CONVERTED CLOSET" cropped out of the full
              logo, never re-typeset. */}
          <img
            className="signin__logo-ring"
            src="/logos/cc-logo-icon-white.png"
            alt=""
          />
          <span className="signin__logo-wordmark">
            <img src="/logos/cc-logo-full-white.png" alt="" />
          </span>
        </div>
      </aside>

      <main className="signin__form-side">
        <div className="signin__inner">
        {status !== 'sent' && (
          <>
            <p className="kicker signin__eyebrow">Conversion Club</p>
            <h1 className="signin__title">
              The room <em>behind the Instagram.</em>
            </h1>
          </>
        )}

        {status !== 'sent' && status !== 'reset-sent' && status !== 'not-invited' && (
          <>
            {mode !== 'forgot' && (
              <div className="signin__tabs" role="tablist" aria-label="Sign-in method">
                <button
                  type="button"
                  role="tab"
                  aria-selected={mode === 'password'}
                  className={`signin__tab ${mode === 'password' ? 'signin__tab--active' : ''}`}
                  onClick={() => { setMode('password'); setStatus('idle'); setErrorText(''); }}
                >
                  Email + password
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={mode === 'magic'}
                  className={`signin__tab ${mode === 'magic' ? 'signin__tab--active' : ''}`}
                  onClick={() => { setMode('magic'); setStatus('idle'); setErrorText(''); }}
                >
                  One Time Link
                </button>
              </div>
            )}

            {mode === 'password' ? (
              <form className="signin__form" onSubmit={submitPassword}>
                <label htmlFor="si-email" className="visually-hidden">Email address</label>
                <input
                  id="si-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  required
                  placeholder="your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <label htmlFor="si-password" className="visually-hidden">Password</label>
                <input
                  id="si-password"
                  type="password"
                  autoComplete="current-password"
                  required
                  minLength={8}
                  placeholder="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="submit"
                  className="signin__submit"
                  disabled={!email || !password || status === 'sending'}
                >
                  {status === 'sending' ? 'Signing in…' : 'Sign in'}
                </button>
                {status === 'error' && <p className="signin__status signin__status--error">{errorText}</p>}
                <p className="signin__forgot">
                  <button
                    type="button"
                    className="signin__linkbtn"
                    onClick={() => { setMode('forgot'); setPassword(''); setStatus('idle'); setErrorText(''); }}
                  >
                    Forgot password?
                  </button>
                </p>
              </form>
            ) : mode === 'magic' ? (
              <form className="signin__form" onSubmit={submitMagic}>
                <label htmlFor="si-email-m" className="visually-hidden">Email address</label>
                <input
                  id="si-email-m"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  required
                  placeholder="your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <button
                  type="submit"
                  className="signin__submit"
                  disabled={!email || status === 'sending'}
                >
                  {status === 'sending' ? 'Sending…' : 'Send one time link'}
                </button>
                {status === 'error' && <p className="signin__status signin__status--error">{errorText}</p>}
              </form>
            ) : (
              <form className="signin__form" onSubmit={submitForgot}>
                <p className="signin__forgot-lede">
                  Enter your email and we'll send you a link to set a new password.
                </p>
                <label htmlFor="si-email-f" className="visually-hidden">Email address</label>
                <input
                  id="si-email-f"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  required
                  placeholder="your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <button
                  type="submit"
                  className="signin__submit"
                  disabled={!email || status === 'sending'}
                >
                  {status === 'sending' ? 'Sending…' : 'Send reset link'}
                </button>
                {status === 'error' && <p className="signin__status signin__status--error">{errorText}</p>}
                <p className="signin__forgot">
                  <button
                    type="button"
                    className="signin__linkbtn"
                    onClick={() => { setMode('password'); setStatus('idle'); setErrorText(''); }}
                  >
                    ← Back to sign in
                  </button>
                </p>
              </form>
            )}

          </>
        )}

        {status === 'sent' && (
          <div className="signin__sent">
            <p className="kicker">Check your inbox</p>
            <p className="signin__sent-line">Click the link and you're in. The tab closes itself.</p>
          </div>
        )}

        {status === 'reset-sent' && (
          <div className="signin__sent">
            <p className="kicker">Check your inbox</p>
            <h2>A reset link is on its way.</h2>
            <p>Click it and you'll be taken to a page to set a new password.</p>
          </div>
        )}

        {status === 'not-invited' && (
          <div className="signin__denied">
            <h2>This email hasn't been invited yet.</h2>
            <p>
              If you were expecting access, write to <a href="mailto:hello@convertedcloset.com">hello@convertedcloset.com</a>.
              Otherwise, the Club opens publicly shortly: <a href="https://convertedcloset.com">join the waitlist</a>.
            </p>
          </div>
        )}
        </div>
      </main>
    </div>
  );
}
